import { useCallback, useEffect, useRef, useState } from "react";
import { MAX_POLLS, POLL_MS, pollDelay } from "../../../../utils/perfPolling";
import { teamKey } from "../coachUtils";
import { fetchCoachTeamPerformance } from "./teamPerformanceServices";

// The Performance tab's game analytics (coach/team/performance/), the
// useTeamStyle pattern: asked once per team and season the first time
// `enabled` holds and kept for the visit; each answer is stored under its
// own key, so a late reply for a previous team never shows on the current
// one. It never throws: a failed request reads as feed_error.
//
// While the server is still building the squad's numbers (HTTP 202) it asks
// again every POLL_MS (or the answer's retryAfter seconds), at most
// MAX_POLLS times (utils/perfPolling, the athlete section's cadence too);
// after that the answer stays "building" with `exhausted` set, and retry()
// asks afresh. While enabled without a `team` yet (its roster is still
// loading) it reads as loading and asks nothing.
//
//   { perf, loading, building, retry }
//   perf      the last answer for this team and season (null before one)
//   loading   enabled and no answer yet
//   building  the server is building and the hook is still polling

export { MAX_POLLS, POLL_MS };
const UNAVAILABLE = { available: false, status: "ready", reason: "feed_error" };

const isBuilding = (answer) => answer?.status === "building";

const useTeamPerformance = ({ team, season, enabled }) => {
  const key = `${teamKey(team)}|${season || ""}`;
  const [answers, setAnswers] = useState({});
  const [attempt, setAttempt] = useState(0);
  const requested = useRef(new Set());
  // One generation per request of a key: a retry starts a new one, and an
  // older request's answer or poll is dropped.
  const generations = useRef({});
  const timers = useRef(new Set());
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    const pending = timers.current;
    return () => {
      alive.current = false;
      pending.forEach((t) => clearTimeout(t));
      pending.clear();
    };
  }, []);

  useEffect(() => {
    if (!enabled || !team || requested.current.has(key)) return;
    requested.current.add(key);
    const generation = (generations.current[key] || 0) + 1;
    generations.current[key] = generation;
    const current = () => alive.current && generations.current[key] === generation;
    const ask = (polls) => {
      fetchCoachTeamPerformance(team, season)
        .catch(() => UNAVAILABLE)
        .then((raw) => {
          if (!current()) return;
          const answer = raw && typeof raw === "object" ? raw : UNAVAILABLE;
          if (isBuilding(answer) && polls < MAX_POLLS) {
            setAnswers((prev) => ({ ...prev, [key]: { ...answer, polls } }));
            const timer = setTimeout(() => {
              timers.current.delete(timer);
              if (current()) ask(polls + 1);
            }, pollDelay(answer.retryAfter));
            timers.current.add(timer);
            return;
          }
          setAnswers((prev) => ({ ...prev, [key]: isBuilding(answer) ? { ...answer, polls, exhausted: true } : answer }));
        });
    };
    ask(0);
    // `key` stands for team and season; `attempt` asks again after retry().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, attempt]);

  // Forget this team and season's answer and ask again (after a failed or
  // a still-building answer).
  const retry = useCallback(() => {
    generations.current[key] = (generations.current[key] || 0) + 1;
    requested.current.delete(key);
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setAttempt((n) => n + 1);
  }, [key]);

  const perf = answers[key] || null;
  return {
    perf,
    loading: Boolean(enabled) && !perf,
    building: Boolean(perf && isBuilding(perf) && !perf.exhausted),
    retry,
  };
};

export default useTeamPerformance;
